/**
 * Mega menu analytics (#313).
 *
 * Pushes three events to the dataLayer for GTM to forward to GA4:
 * - menu_open: a dropdown was opened on purpose
 * - menu_click: a header menu link was clicked
 * - featured_content_impression: a dropdown's featured card was shown
 *
 * Featured card clicks stay on the GTM click trigger that sends
 * featured_content_click (#299), so this module ignores them.
 *
 * Desktop dropdowns open instantly on hover and focus, so an open counts only
 * after the dropdown stays open for OPEN_DWELL_MS. Crossing the nav on the way
 * to search or the language switcher doesn't count. On mobile, the featured
 * card sits below the links, so its impression waits until it scrolls into view.
 *
 * Every push sets all of its event's parameters, even when empty. GTM merges
 * pushes into one data model, so an omitted key would keep the value from the
 * previous event.
 */
const OPEN_DWELL_MS = 500;
const DESKTOP_NAV = 'header.o-header .m-menu--main-menu.m-menu-desktop';
const MOBILE_NAV = '.o-mobile-menu';
const FEATURED_CARD = '.mega-menu__featured-card';

function push(event, params) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
}

function cleanText(el) {
  return (el?.textContent || '').replace(/\s+/g, ' ').trim();
}

// "dropdown-products" and "dropdown-mobile-products" both become "products".
function slugFromTrigger(trigger) {
  return (trigger.getAttribute('aria-controls') || '').replace(/^dropdown-(mobile-)?/, '');
}

function init() {
  const desktopNav = document.querySelector(DESKTOP_NAV);
  const mobileNav = document.querySelector(MOBILE_NAV);
  if (!desktopNav && !mobileNav) return;

  // The input that last moved focus or the pointer decides open_method.
  let lastInput = 'hover';
  document.addEventListener('keydown', () => { lastInput = 'keyboard'; }, true);
  document.addEventListener('pointerdown', e => {
    lastInput = e.pointerType === 'mouse' ? 'hover' : 'tap';
  }, true);
  document.addEventListener('pointerover', e => {
    if (e.pointerType === 'mouse') lastInput = 'hover';
  }, true);

  // One impression per dropdown and device per page view.
  const impressed = new Set();

  function pushImpression(card, slug, device) {
    const key = `${device}:${slug}`;
    if (impressed.has(key)) return;
    impressed.add(key);
    push('featured_content_impression', {
      menu_slug: slug,
      menu_device: device,
      featured_slot: card.dataset.featuredSlot || '',
      featured_url: card.href || '',
    });
  }

  function watchOpens(li, device, onOpen) {
    const trigger = li.querySelector(':scope > a[aria-controls]');
    if (!trigger) return;
    const slug = slugFromTrigger(trigger);
    let dwellTimer = null;

    // Navigation.js sets aria-expanded on every hover and focus, so only a
    // change from closed to open counts.
    new MutationObserver(records => {
      const isOpen = li.getAttribute('aria-expanded') === 'true';
      const wasOpen = records[0].oldValue === 'true';
      if (isOpen === wasOpen) return;

      clearTimeout(dwellTimer);
      if (!isOpen) return;

      const method = device === 'mobile' && lastInput === 'hover' ? 'tap' : lastInput;
      const report = () => {
        push('menu_open', { menu_slug: slug, menu_device: device, open_method: method });
        onOpen?.(slug);
      };
      if (device === 'desktop') {
        dwellTimer = setTimeout(() => {
          if (li.getAttribute('aria-expanded') === 'true') report();
        }, OPEN_DWELL_MS);
      } else {
        report();
      }
    }).observe(li, { attributes: true, attributeFilter: ['aria-expanded'], attributeOldValue: true });

    return slug;
  }

  desktopNav?.querySelectorAll(':scope > li.has-mega-menu').forEach(li => {
    watchOpens(li, 'desktop', slug => {
      const card = li.querySelector(FEATURED_CARD);
      if (card) pushImpression(card, slug, 'desktop');
    });
  });

  // A closed accordion or the off-canvas menu never intersects the viewport,
  // so a visible card is a shown card.
  const cardObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          cardObserver.unobserve(entry.target);
          pushImpression(entry.target, entry.target.dataset.menuSlug, 'mobile');
        });
      }, { threshold: 0.5 })
    : null;

  mobileNav?.querySelectorAll('li.has-mega-menu').forEach(li => {
    const slug = watchOpens(li, 'mobile');
    const card = li.querySelector(FEATURED_CARD);
    if (slug && card && cardObserver) {
      card.dataset.menuSlug = slug;
      cardObserver.observe(card);
    }
  });

  function trackClick(e, device) {
    const link = e.target.closest('a[href]');
    if (!link || link.matches(FEATURED_CARD) || link.matches('[aria-controls]')) return;
    if (link.closest('.trp-language-switcher, [class*="trp-language"]')) return;

    let slug = 'top-level';
    const dropdownLi = link.closest('li.has-mega-menu');
    if (dropdownLi) {
      const trigger = dropdownLi.querySelector(':scope > a[aria-controls]');
      if (trigger) slug = slugFromTrigger(trigger);
    } else if (link.matches('.m-menu-cta')) {
      slug = 'cta';
    }

    const group = link.closest('.mega-menu__group');
    push('menu_click', {
      menu_slug: slug,
      menu_group: cleanText(group?.querySelector('.mega-menu__heading')),
      menu_device: device,
      link_url: link.href,
      link_text: cleanText(link.querySelector('.mega-menu__title') || link),
    });
  }

  desktopNav?.addEventListener('click', e => trackClick(e, 'desktop'));
  mobileNav?.addEventListener('click', e => trackClick(e, 'mobile'));
}

export { init as default };
